/**
 * Shared building blocks for Resonance's centered dialog modals, so every
 * modal draws from the study theme (theme/tokens.ts roles) instead of
 * hand-rolled grays:
 *   card    -> background token, same 28px card language as the account page
 *   ink     -> text token (titles full-strength, descriptions softened)
 *   main action  -> secondary pill (the app's CTA family)
 *   quiet action -> accent text (the app's link family)
 *   alt action   -> accent outline pill (e.g. "resend code")
 * Bottom sheets (.resonance-sheetmodal) get their chrome from theme/modal.scss
 * instead — don't use ModalCard inside a sheet.
 */
import styled, { keyframes } from "styled-components";
import { FieldError, FieldLabel, StyledTextField } from "./LoginTextField";
import { SelectFieldTrigger } from "./SelectField";

/** Fills the IonModal viewport and centers the card. Modals usually put their
 *  backdrop-dismiss onClick here and stopPropagation on the card. */
export const ModalOverlay = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
`;

export const ModalCard = styled.div`
  width: 100%;
  max-width: 420px;
  max-height: 85vh;
  overflow-y: auto;
  background-color: var(--app-color-background, #ffffff);
  border: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  border-radius: 28px;
  box-shadow: 0 18px 40px rgba(var(--app-color-text-rgb, 4, 23, 35), 0.16);
  padding: 24px 22px;
`;

export const ModalTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  color: var(--app-color-text, #041723);
`;

export const ModalDescription = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.65);
`;

/** Softened helper line (countdowns, hints) — same ink as ModalDescription
 *  but inline-sized and left-aligned for placement under a field. */
export const ModalMeta = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.65);
`;

export const ModalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

/** Main confirming action — CTA family (secondary pill, like the app's buttons). */
export const ModalPrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 22px;
  border: none;
  border-radius: 999px;
  background: var(--app-color-secondary, #fecf48);
  color: var(--app-color-secondary-contrast, #041723);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    background: var(--app-color-secondary-shade, #e0b63f);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Quiet dismiss/secondary action (Annuler/Fermer) — softened text ink, NOT a
 *  hue: the row's hierarchy comes from shape (filled pill vs plain text), and
 *  a hue-free cancel can never clash with whatever accent+secondary combo a
 *  researcher picks. Semibold keeps it clearly interactive vs disabled (0.35). */
export const ModalQuietButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.7);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.35);
    cursor: not-allowed;
  }
`;

/** Outlined alternative action (e.g. "resend code") — accent outline pill. */
export const ModalOutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid var(--app-color-accent, #4285f4);
  border-radius: 999px;
  background: transparent;
  color: var(--app-color-accent, #4285f4);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    border-color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.2);
    color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.35);
    background: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.04);
    cursor: not-allowed;
  }
`;

/** Inline error line under the fields. */
export const ModalError = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme?.danger?.main || "#d64545"};
`;

const fieldLabelIn = keyframes`
  from { opacity: 0; transform: translateY(2px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/**
 * Wrap a dialog's <TextField>s in this — it restyles the login-screen pill
 * (LoginTextField) into a flat, bordered field for the card context. The pill
 * only reads on the colored login background; on a card it dissolves into a
 * blob, because its fill is the surface token and several themes set
 * surface == background. Here BOTH fill and hairline derive from the TEXT
 * token at low alpha, so the field self-contrasts under any researcher theme.
 * Login screens are untouched (design panel synthesis, 2026-07-03).
 */
export const ModalFields = styled.div`
  ${StyledTextField} {
    background-color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.04);
    border: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.12);
    border-radius: 14px; /* half the 28px card: clearly a field, not a pill */
    box-shadow: none;    /* the card owns elevation; the field is flat */
    height: auto;        /* kills the viewport-dependent height: 5vh */
    min-height: 52px;    /* fixed, comfortably over the 44px touch target */
    box-sizing: border-box;
    padding: 8px 16px;
    margin: 12px 0 0;
    font-size: 16px;
    /* beats the login pill's error "red !important" — the typed value stays
       readable ink; the error state lives on the border/icon/message instead */
    color: var(--app-color-text, #454545) !important;
    transition: border-color 120ms ease-out, box-shadow 120ms ease-out;

    input {
      font-size: 16px; /* >= 16px: no iOS Safari zoom-on-focus */
      font-weight: 400;
      line-height: 1.3;
      caret-color: var(--app-color-accent, #4285f4);
    }
    input::placeholder {
      color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.5) !important;
    }

    /* Focus on the container (:focus-within — the inner input is outline:none):
       accent border + soft accent ring, theme-driven, breathing in through the
       120ms transition (WCAG 2.4.7 visibility for keyboard/desktop). */
    &:focus-within {
      border-color: var(--app-color-accent, #4285f4);
      box-shadow: 0 0 0 3px rgba(var(--app-color-accent-rgb, 66, 133, 244), 0.2);
    }
    &:focus-within ${FieldLabel} {
      color: var(--app-color-accent, #4285f4);
    }

    /* Error = danger frame + icon + message (GOV.UK anatomy); keys off the
       aria-invalid LoginTextField sets. If :has() is unsupported the danger
       icon + message below still fully signal the error. */
    &:has(input[aria-invalid="true"]) {
      border-color: ${({ theme }) => theme?.danger?.main || "#d64545"};
    }
    &:has(input[aria-invalid="true"]) ${FieldLabel} {
      color: ${({ theme }) => theme?.danger?.main || "#d64545"};
    }
    /* Error + focus: border stays danger, ring turns danger — focus visibility
       never disappears because the field is invalid. */
    &:has(input[aria-invalid="true"]):focus-within {
      box-shadow: 0 0 0 3px rgba(214, 69, 69, 0.18);
    }
  }

  ${FieldLabel} {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.6);
    margin: 0 0 1px;
    animation: ${fieldLabelIn} 120ms ease-out;
  }

  ${FieldError} {
    position: static;     /* drop the pill variant's stray position: relative */
    margin: 6px 0 0 16px; /* hangs under its field, aligned to the input inset */
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
  }

  /* SelectField's trigger row (an underlined bar elsewhere) joins the same
     flat-field language when it sits among dialog fields. */
  ${SelectFieldTrigger} {
    background-color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.04);
    border: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.12);
    border-radius: 14px;
    min-height: 52px;
    box-sizing: border-box;
    padding: 8px 16px;
    margin: 12px 0 0;
    font-size: 16px;
    transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
  }

  /* Actions read as their own section against the tightened 12px field rhythm. */
  & + ${ModalActions} {
    margin-top: 24px;
  }
`;
